import {
  AttachedProgressBar,
  Box,
  Divider,
  Flex,
  getOrderStatusColor,
  isAgGridColumnVisible,
  MobileDataTableCardSurface,
  prettyWarningSeverity,
  Text,
  useIntl,
  WarningSeverityIcon,
  type MobileCardRendererProps,
  type Order,
} from '@talos/kyoko';
import styled, { useTheme } from 'styled-components';
import { ORDER_COLUMN_IDS } from '../../../../Blotters/Orders/orderColumnIds';
import { useCompactMobileCardLayout } from '../useCompactMobileCardLayout';

export const MOBILE_ORDER_CARD_HEIGHT = 126;
export const COMPACT_MOBILE_ORDER_CARD_HEIGHT = 82;

export function useMobileOrderCardLayout() {
  return useCompactMobileCardLayout(MOBILE_ORDER_CARD_HEIGHT, COMPACT_MOBILE_ORDER_CARD_HEIGHT);
}

/**
 * Mobile card for a single Order. Designed to be passed to `MobileDataTable`
 * via the `cardRenderer` prop.
 *
 * Layout follows the design:
 *  - Header row: symbol icon + symbol + strategy, with status badge on right
 *  - Meta row: submit time and short order ID
 *  - Divider
 *  - Side + qty @ price
 *  - Filled row (when there are fills: cum qty @ avg price)
 *  - Fill progress bar with percentage (when fill data is available)
 *  - Attached fill progress strip along the bottom edge (compact)
 */
export function MobileOrderCard({ data, api, renderCell, onPress, compact = false }: MobileCardRendererProps<Order>) {
  const intl = useIntl();
  const theme = useTheme();
  const showWarningColumn = isAgGridColumnVisible(api.getColumn(ORDER_COLUMN_IDS.Warning));
  const warning = showWarningColumn ? data.warning : undefined;
  const hasWarning = warning != null;
  const fillColor = getOrderStatusColor(data, false, theme);

  return (
    <MobileDataTableCardSurface
      type="button"
      data-testid="mobile-order-card"
      onClick={onPress}
      $compact={compact}
      $gap={4}
    >
      <Flex justifyContent="space-between" alignItems="center" gap="spacing4">
        <Flex alignItems="center" gap="spacing4" overflow="hidden">
          <Box
            flex="1 1 auto"
            overflow="hidden"
            textOverflow="clip"
            whiteSpace="nowrap"
            data-testid="mobile-order-card-symbol"
          >
            {renderCell(ORDER_COLUMN_IDS.Symbol)}
          </Box>
          <Box
            flex="1 1 auto"
            overflow="hidden"
            textOverflow="clip"
            whiteSpace="nowrap"
            data-testid="mobile-order-card-strategy"
            textTransform="uppercase"
            color="colorTextSubtle"
          >
            {renderCell(ORDER_COLUMN_IDS.Strategy)}
          </Box>
          {hasWarning && (
            <WarningCell data-testid="mobile-order-card-warning">
              <WarningSeverityIcon
                severity={warning.severity}
                data-testid="warning-bar"
                aria-label={prettyWarningSeverity(warning.severity, intl)}
              />
            </WarningCell>
          )}
        </Flex>
        <Box data-testid="mobile-order-card-status" flex="0 0 auto">
          {renderCell(ORDER_COLUMN_IDS.OrdStatus)}
        </Box>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" fontSize="fontSizeTiny">
        <Box data-testid="mobile-order-card-submit-time">{renderCell(ORDER_COLUMN_IDS.SubmitTime)}</Box>
        <Flex alignItems="center" gap="spacing4">
          <Text data-testid="mobile-order-card-id-label" color="colorTextSubtle">
            ID
          </Text>
          <Box data-testid="mobile-order-card-order-id">{renderCell(ORDER_COLUMN_IDS.OrderID)}</Box>
        </Flex>
      </Flex>

      {!compact && <Divider color="backgroundDivider" />}

      <Flex justifyContent="space-between" alignItems="center" fontSize="fontSizeSmall">
        <Box data-testid="mobile-order-card-side">{renderCell(ORDER_COLUMN_IDS.Side)}</Box>
        <Flex alignItems="center" gap="spacing4" overflow="hidden">
          <Box data-testid="mobile-order-card-order-qty">{renderCell(ORDER_COLUMN_IDS.OrderQty)}</Box>
          <Text color="colorTextSubtle" fontWeight={500}>
            @
          </Text>
          <Box data-testid="mobile-order-card-price">{renderCell(ORDER_COLUMN_IDS.Price)}</Box>
        </Flex>
      </Flex>

      {!compact && (
        <Flex justifyContent="space-between" alignItems="center" fontSize="fontSizeSmall">
          <Text data-testid="mobile-order-card-filled-label" color="colorTextSubtle">
            FILLED
          </Text>
          <Flex alignItems="center" gap="spacing4" overflow="hidden">
            <Box data-testid="mobile-order-card-cum-qty">{renderCell(ORDER_COLUMN_IDS.CumQty)}</Box>
            <Text color="colorTextSubtle" fontWeight={500}>
              @
            </Text>
            <Box data-testid="mobile-order-card-avg-px">
              {renderCell(ORDER_COLUMN_IDS.AvgPx, { overrideColumnVisibility: true })}
            </Box>
          </Flex>
        </Flex>
      )}

      {!compact && data.CumQty != null && data.OrderQty != null && renderCell(ORDER_COLUMN_IDS.FilledPercent)}

      {compact && <AttachedProgressBar value={data.CumQty} max={data.OrderQty} color={fillColor} />}
    </MobileDataTableCardSurface>
  );
}

const WarningCell = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;
